/**
 * Links to the files a meeting was scheduled with (VAPT 2026-10 §6.2).
 *
 * Those files are stored private: CloudFront opens them only through a signed
 * URL the backend gives the meeting's host, invitees and co-hosts, and the URL
 * lapses within minutes. So a scheduled file is never opened by the URL saved
 * on the meeting. Present and Download each ask for a link at that moment, and
 * a presentation that outlives its link is handed a fresh one before it lapses,
 * so whoever joins later still gets a link that opens.
 *
 * Anything else in the documents list (a file uploaded during the meeting)
 * carries no `meetingId` and keeps the URL it already has.
 */

import { meetingsApi } from '@/features/meetings/services/meetings.api'

export interface FileLink {
  url: string
  /** Epoch ms it stops opening; null for a link that does not lapse. */
  expiresAt: number | null
}

export type FetchLink = (
  meetingId: string,
  attachmentId: string,
) => Promise<{ url: string | null; expiresAt: number | null }>

/** What the documents list knows about a file. */
export interface DocumentForLink {
  id: string
  url: string
  previewUrl?: string
  previewAttachmentId?: string
  /** Set on a file the meeting was scheduled with. */
  meetingId?: string
}

/** Ask the backend for a link. Throws its refusal (403 for anyone not invited). */
export const fetchScheduledFileLink: FetchLink = async (meetingId, attachmentId) => {
  const r = await meetingsApi.getAttachmentUrl(meetingId, attachmentId)
  const expiresAt = r.expiresAt ? Date.parse(r.expiresAt) : NaN
  return { url: r.url, expiresAt: Number.isFinite(expiresAt) ? expiresAt : null }
}

/**
 * The URL to open a document with. Present shows a PowerPoint, Word or text
 * file as its PDF copy; Download takes the original.
 */
export async function documentLink(
  doc: DocumentForLink,
  use: 'present' | 'download',
  fetchLink: FetchLink = fetchScheduledFileLink,
): Promise<FileLink> {
  const held = use === 'present' ? doc.previewUrl ?? doc.url : doc.url
  if (!doc.meetingId) return { url: held, expiresAt: null }
  const attachmentId = use === 'present' ? doc.previewAttachmentId ?? doc.id : doc.id
  const link = await fetchLink(doc.meetingId, attachmentId)
  // No URL back means local storage, where the held one is the way in.
  return { url: link.url ?? held, expiresAt: link.expiresAt }
}

/** Ask for the next link this long before the current one lapses. */
export const REFRESH_AHEAD_MS = 60_000
/** Never sooner than this, and how long to wait after a failed ask. */
export const MIN_REFRESH_MS = 10_000

/** When to fetch the next link; null for a link that never lapses. */
export function refreshDelayMs(expiresAt: number | null, now: number): number | null {
  if (expiresAt === null) return null
  return Math.max(expiresAt - now - REFRESH_AHEAD_MS, MIN_REFRESH_MS)
}

/**
 * Keep a presented scheduled file's link from lapsing: shortly before it does,
 * fetch another and pass it to `onUrl` (which re-announces the share, so the
 * room's copy for late joiners opens). Stops by itself once `stillPresenting`
 * says no, and on the returned stop function.
 */
export function keepLinkFresh(opts: {
  doc: DocumentForLink
  expiresAt: number | null
  stillPresenting: () => boolean
  onUrl: (url: string) => void
  fetchLink?: FetchLink
  now?: () => number
}): () => void {
  const now = opts.now ?? Date.now
  let timer: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  const stop = () => {
    stopped = true
    if (timer) clearTimeout(timer)
    timer = null
  }

  const schedule = (delay: number | null) => {
    if (stopped || delay === null) return
    timer = setTimeout(() => {
      timer = null
      if (stopped) return
      if (!opts.stillPresenting()) return stop()
      void documentLink(opts.doc, 'present', opts.fetchLink).then(
        (link) => {
          if (stopped || !opts.stillPresenting()) return stop()
          opts.onUrl(link.url)
          schedule(refreshDelayMs(link.expiresAt, now()))
        },
        // The link in the room is still good for up to a minute; try again.
        () => schedule(MIN_REFRESH_MS),
      )
    }, delay)
  }

  schedule(refreshDelayMs(opts.expiresAt, now()))
  return stop
}
